import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Eye, EyeOff, Lock } from 'lucide-react';
import { CARRIER_LABELS, DIRECTIONS, DIRECTION_LABELS, carrierLabel } from '../../constants/shipments';
import { camelize, entityPath, formatDate, formatDateTime, formatMoney, formatPercent } from '../../utils/shipmentsFormat';
import EditableField from './EditableField';
import { StatusBadge } from './StatusBadge';
import { CopyButton, ExternalLinkButton } from './Ui';

const MATCH_LABELS = { none: 'Bez vazby na ERP', suggested: 'Návrh čeká na potvrzení', confirmed: 'Potvrzeno' };
const GROUPS = ['Zásilka', 'Strany', 'Termíny a dobírka', 'Vazby na ERP'];

const blank = (v) => v == null || v === '';

// Entity as a link (customer, machine have a detail page) or plain text; only numeric ids ever reach a URL.
const EntityValue = ({ kind, entity, label }) => {
  if (!entity) return null;
  const path = entityPath(kind, entity.id);
  return path ? <Link to={path}>{label}</Link> : <span>{label}</span>;
};

const Row = ({ label, children }) => <div className="shp-dl__row"><dt>{label}</dt><dd>{children}</dd></div>;

// Data card of the shipment detail, grouped (Zásilka / Strany / Termíny a dobírka / Vazby na ERP). Empty fields are
// hidden unless "Zobrazit prázdná pole" is on; in edit mode everything is visible. Every edited field is locked ("ručně").
const DetailDataCard = ({ shipment: s, carriers, editMode, readOnly, onPatch, onUnlock }) => {
  const [showEmpty, setShowEmpty] = useState(false);
  const manual = new Set((s.manualFields || []).map(camelize));
  const editable = editMode && !readOnly;
  const f = (field, extra = {}) => ({ field, locked: manual.has(field), editable, onSave: onPatch, onUnlock, ...extra });
  const carrierOptions = (carriers?.length ? carriers.map((c) => ({ value: c.code, label: c.name || CARRIER_LABELS[c.code] || c.code }))
    : Object.entries(CARRIER_LABELS).map(([value, label]) => ({ value, label })));

  // [group index, is the value empty, row element]. The status badge is already in the page header, so the "Stav" row
  // only appears when there is something to say about it (manual lock) or in edit mode / with the empty fields.
  const fields = [
    [0, s.statusSource !== 'manual', (
      <Row key="status" label="Stav">
        <div className="shp-dl__view">
          <span className="shp-dl__value"><StatusBadge status={s.status} /></span>
          {s.statusSource === 'manual' && (
            <span className="shp-dl__tools">
              <span className="shp-tag shp-tag--info" title="Stav byl změněn ručně, synchronizace ho nepřepíše."><Lock size={12} aria-hidden="true" /> ručně</span>
              {editable && <button type="button" className="shp-textbtn" onClick={() => onUnlock('status')} aria-label="Odemknout pole: Stav">Odemknout</button>}
            </span>
          )}
        </div>
      </Row>
    )],
    [0, false, (
      <Row key="tracking" label="Číslo zásilky">
        <div className="shp-dl__view">
          <span className="shp-dl__value shp-wrap" dir="auto">{s.trackingNumber || <span className="shp-muted">Čeká na tracking</span>}</span>
          <span className="shp-dl__tools">
            {s.trackingNumber && <CopyButton value={s.trackingNumber} />}
            <ExternalLinkButton url={s.trackingUrl}>Sledovat u dopravce</ExternalLinkButton>
          </span>
        </div>
      </Row>
    )],
    [0, false, <EditableField key="carrier" label="Dopravce" kind="select" value={s.carrierCode} display={carrierLabel(s.carrierCode, s.carrierName)} options={carrierOptions} {...f('carrierCode')} />],
    [0, blank(s.direction), <EditableField key="direction" label="Směr" kind="select" value={s.direction} display={DIRECTION_LABELS[s.direction]} options={DIRECTIONS} {...f('direction')} />],
    [0, blank(s.orderRef), <EditableField key="orderRef" label="Číslo objednávky" value={s.orderRef} display={s.orderRef} {...f('orderRef')} />],
    [0, blank(s.invoiceRef), <EditableField key="invoiceRef" label="Faktura" value={s.invoiceRef} display={s.invoiceRef} {...f('invoiceRef')} />],
    [0, blank(s.description), <EditableField key="description" label="Popis zboží" kind="textarea" value={s.description} display={s.description} {...f('description')} />],
    [0, blank(s.pickupPoint), <EditableField key="pickupPoint" label="Výdejní místo" value={s.pickupPoint} display={s.pickupPoint} {...f('pickupPoint')} />],
    [0, s.confidence == null, <Row key="confidence" label="Jistota AI">{formatPercent(s.confidence)}</Row>],
    [0, false, <Row key="created" label="Vytvořeno">{formatDateTime(s.createdAt)}</Row>],
    [0, !s.lastEventAt, <Row key="last" label="Poslední událost">{formatDateTime(s.lastEventAt)}</Row>],

    [1, blank(s.senderName), <EditableField key="sender" label="Odesílatel" value={s.senderName} display={s.senderName} {...f('senderName')} />],
    [1, blank(s.recipientName), <EditableField key="recipient" label="Příjemce" value={s.recipientName} display={s.recipientName} {...f('recipientName')} />],

    [2, blank(s.expectedDelivery), <EditableField key="expected" label="Očekávané doručení" kind="date" value={s.expectedDelivery ? s.expectedDelivery.slice(0, 10) : ''} display={formatDate(s.expectedDelivery)} {...f('expectedDelivery')} />],
    [2, blank(s.deliveredAt), <EditableField key="delivered" label="Doručeno dne" kind="date" value={s.deliveredAt ? s.deliveredAt.slice(0, 10) : ''} display={formatDate(s.deliveredAt)} {...f('deliveredAt')} />],
    [2, blank(s.codAmount), (
      <EditableField
        key="cod" label="Dobírka" kind="cod" value={s.codAmount} codCurrency={s.codCurrency || 'CZK'}
        display={s.codAmount != null ? formatMoney(s.codAmount, s.codCurrency) : null}
        {...f('codAmount', { locked: manual.has('codAmount') || manual.has('codCurrency') })}
      />
    )],

    [3, !s.customer, <EditableField key="customer" label="Zákazník" kind="entity" entityKind="customer" value={s.customer?.id ?? ''} current={{ name: s.customer?.name }}
      display={<EntityValue kind="customer" entity={s.customer} label={s.customer?.name} />} {...f('customerId')} />],
    [3, !s.supplier, <EditableField key="supplier" label="Dodavatel" kind="entity" entityKind="supplier" value={s.supplier?.id ?? ''} current={{ name: s.supplier?.name }}
      display={<EntityValue kind="supplier" entity={s.supplier} label={s.supplier?.name} />} {...f('supplierId')} />],
    [3, !s.machine, <EditableField key="machine" label="Trenažér" kind="entity" entityKind="machine" value={s.machine?.id ?? ''} current={{ name: s.machine?.serialNumber }}
      display={<EntityValue kind="machine" entity={s.machine} label={s.machine?.serialNumber} />} {...f('machineId')} />],
    [3, !s.orderBatch, <EditableField key="order" label="Objednávka dílů" kind="entity" entityKind="order" value={s.orderBatch?.id ?? ''} current={{ name: s.orderBatch?.orderNumber }}
      display={<EntityValue kind="order" entity={s.orderBatch} label={s.orderBatch?.orderNumber} />} {...f('orderBatchId')} />],
    [3, false, <Row key="match" label="Párování s ERP">{MATCH_LABELS[s.matchState] || '–'}</Row>],
  ];

  const visible = (empty) => !empty || editable || showEmpty;
  const hiddenCount = fields.filter(([, empty]) => empty && !visible(empty)).length;
  const canToggle = !editable && (hiddenCount > 0 || showEmpty);

  return (
    <section className="shp-card" aria-labelledby="shp-data-title">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
        <h2 id="shp-data-title" className="shp-h2" style={{ margin: 0 }}>Údaje o zásilce</h2>
        {canToggle && (
          <button type="button" className="shp-textbtn" aria-pressed={showEmpty} onClick={() => setShowEmpty((v) => !v)}>
            {showEmpty ? <EyeOff size={15} aria-hidden="true" /> : <Eye size={15} aria-hidden="true" />}
            {showEmpty ? 'Skrýt prázdná pole' : `Zobrazit prázdná pole (${hiddenCount})`}
          </button>
        )}
      </div>
      {GROUPS.map((title, gi) => {
        const rows = fields.filter(([g, empty]) => g === gi && visible(empty));
        if (rows.length === 0) return null;
        return (
          <div key={title}>
            <h3 className="shp-h3">{title}</h3>
            <dl className="shp-dl">{rows.map(([, , el]) => el)}</dl>
          </div>
        );
      })}
    </section>
  );
};

export default DetailDataCard;
